import type { PropType } from 'vue'
import { computed, useSlots } from 'vue'

import BaseIcon from '@/components/BaseIcon.vue'
import { Space } from '@/components/Space'
import { variants } from '@/utils/variants'

import type { VariantPropsOf } from '@/utils/variants'

import './style.css'

const styles = variants({
  base: 'styled-button',
  compoundVariants: [
    {
      className: 'styled-button__compound--primary-default',
      variants: {
        type: 'default',
        variant: 'primary',
      },
    },
    {
      className: 'styled-button__compound--primary-warning',
      variants: {
        type: 'warning',
        variant: 'primary',
      },
    },
    {
      className: 'styled-button__compound--primary-error',
      variants: {
        type: 'error',
        variant: 'primary',
      },
    },
    {
      className: 'styled-button__compound--primary-purple',
      variants: {
        type: 'purple',
        variant: 'primary',
      },
    },
    {
      className: 'styled-button__compound--secondary-default',
      variants: {
        type: 'default',
        variant: 'secondary',
      },
    },
    {
      className: 'styled-button__compound--secondary-warning',
      variants: {
        type: 'warning',
        variant: 'secondary',
      },
    },
    {
      className: 'styled-button__compound--secondary-error',
      variants: {
        type: 'error',
        variant: 'secondary',
      },
    },
    {
      className: 'styled-button__compound--secondary-purple',
      variants: {
        type: 'purple',
        variant: 'secondary',
      },
    },
    {
      className: 'styled-button__compound--tertiary-default',
      variants: {
        type: 'default',
        variant: 'tertiary',
      },
    },
    {
      className: 'styled-button__compound--tertiary-warning',
      variants: {
        type: 'warning',
        variant: 'tertiary',
      },
    },
    {
      className: 'styled-button__compound--tertiary-error',
      variants: {
        type: 'error',
        variant: 'tertiary',
      },
    },
    {
      className: 'styled-button__compound--tertiary-purple',
      variants: {
        type: 'purple',
        variant: 'tertiary',
      },
    },
    {
      className: 'styled-button__compound--outline-default',
      variants: {
        type: 'default',
        variant: 'outline',
      },
    },
    {
      className: 'styled-button__compound--outline-warning',
      variants: {
        type: 'warning',
        variant: 'outline',
      },
    },
    {
      className: 'styled-button__compound--outline-error',
      variants: {
        type: 'error',
        variant: 'outline',
      },
    },
    {
      className: 'styled-button__compound--outline-purple',
      variants: {
        type: 'purple',
        variant: 'outline',
      },
    },
    {
      className: 'styled-button__compound--light-default',
      variants: {
        type: 'default',
        variant: 'light',
      },
    },
    {
      className: 'styled-button__compound--light-warning',
      variants: {
        type: 'warning',
        variant: 'light',
      },
    },
    {
      className: 'styled-button__compound--light-error',
      variants: {
        type: 'error',
        variant: 'light',
      },
    },
    {
      className: 'styled-button__compound--light-purple',
      variants: {
        type: 'purple',
        variant: 'light',
      },
    },
  ],
  defaultVariants: {
    size: 'default',
    type: 'default',
    variant: 'primary',
    width: 'default',
  },
  variants: {
    disabled: {
      true: 'styled-button--disabled',
    },
    size: {
      big: 'styled-button__size--big',
      default: 'styled-button__size--default',
      inline: 'styled-button__size--inline',
      small: 'styled-button__size--small',
    },
    type: {
      default: '',
      error: '',
      purple: '',
      warning: '',
    },
    variant: {
      light: 'styled-button__variant--light',
      outline: 'styled-button__variant--outline',
      primary: 'styled-button__variant--primary',
      secondary: 'styled-button__variant--secondary',
      tertiary: 'styled-button__variant--tertiary',
    },
    width: {
      default: 'styled-button__width--default',
      short: 'styled-button__width--short',
      wide: 'styled-button__width--wide',
    },
  },
})

type Props = VariantPropsOf<typeof styles> & {
  leftIcon?: IconName
  leftIconSize?: IconSize
  rightIcon?: IconName
  rightIconSize?: IconSize
  componentName?: string
}

export const StyledButton = (props: Props) => {
  const slots = useSlots()
  const classes = computed(() => {
    return styles({
      disabled: props.disabled,
      size: props.size,
      type: props.type,
      variant: props.variant,
      width: props.width,
    })
  })

  return (
    <button class={classes.value} data-testId={props.componentName} disabled={props.disabled}>
      <Space alignY="center" direction="columns" space={4}>
        {props.leftIcon && <BaseIcon name={props.leftIcon} size={props.leftIconSize ?? '16'} />}
        {slots.default?.()}
        {props.rightIcon && <BaseIcon name={props.rightIcon} size={props.rightIconSize ?? '16'} />}
      </Space>
    </button>
  )
}

StyledButton.props = {
  componentName: String,
  disabled: Boolean,
  leftIcon: String as PropType<Props['leftIcon']>,
  leftIconSize: String as PropType<Props['leftIcon']>,
  rightIcon: String as PropType<Props['rightIcon']>,
  rightIconSize: String as PropType<Props['rightIcon']>,
  size: String as PropType<Props['size']>,
  type: String as PropType<Props['type']>,
  variant: String as PropType<Props['variant']>,
  width: String as PropType<Props['width']>,
}
